// @ts-check
import { gql, useMutation, useQuery } from '@apollo/client';
import { Plural, Trans } from '@lingui/macro';
import { Form, Formik } from 'formik';
import React from 'react';
import { Box, Text } from '~/design-system';
import ImportOptionsSkeleton from '../ImportOptionsSkeleton';
import ReadyCheckboxField from '../ReadyCheckboxField';

/**
 * Renders the ready to import page with the import statistics
 * The view. The props are passed in from useReady - all the logic resides there.
 * @param {{
 *    setFormikContext: ReturnType<import('../../useFormikOrchestration').default>["setFormikContext"]
 * } & ReturnType<useReady>} props
 * @returns
 */
export default function ReadyToImport({
  importData,
  loading,
  setFormikContext,
  onSubmit,
}) {
  if (loading)
    return (
      <Box>
        <ImportOptionsSkeleton />
      </Box>
    );
  const { preview: previewStatistics } = importData || {};
  return (
    <Box>
      <ImportOptions
        statistics={previewStatistics}
        setFormikContext={setFormikContext}
        onSubmit={onSubmit}
      />
    </Box>
  );
}

function ImportOptions({ statistics, setFormikContext, onSubmit }) {
  const profileStatistics = statistics?.find(
    (stat) => stat.object === 'PROFILE'
  );
  const initialValues = {
    updated: profileStatistics?.updatedCount > 0,
    unchanged: profileStatistics?.unchangedCount > 0,
  };
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(props) => {
        // Map to import-specific flags
        const importOptions = {
          updateProducts: props.updated,
        };
        onSubmit(importOptions);
      }}
    >
      {(formikContext) => {
        setFormikContext(formikContext);
        return (
          <Form>
            <Box>
              <Box sx={{ textAlign: 'center' }}>
                <Text sx={{ marginTop: 3 }}>
                  <Trans>All your data is valid and ready to import.</Trans>
                </Text>
              </Box>
              <Box sx={{ margin: 'auto', width: '330px', textAlign: 'left' }}>
                <ReadyCheckboxField
                  name="updated"
                  count={profileStatistics?.updatedCount}
                  disabled={!initialValues.updated}
                >
                  <Plural
                    value={profileStatistics?.updatedCount}
                    one={'Profile that will be updated'}
                    other={'Profiles that will be updated'}
                  />
                </ReadyCheckboxField>
                <ReadyCheckboxField
                  name="unchanged"
                  disabled
                  count={profileStatistics?.unchangedCount}
                >
                  <Plural
                    value={profileStatistics?.unchangedCount}
                    one={'Profile have no change'}
                    other={'Profiles have no change'}
                  />
                </ReadyCheckboxField>
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}

/**
 * @param {object} props
 * @param {string} props.workspaceId
 * @param {string} props.importId
 */
export function useReady({ workspaceId, importId }) {
  const { data, error, loading } = useQuery(QUERY, {
    variables: { workspaceId, importId },
  });
  const [importExternalIds] = useMutation(IMPORT_PROFILE_DATA);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  return {
    importId,
    ...afterQuery(data),
    error,
    loading,
    isSubmitting,
    onSubmit: async (importOptions) => {
      setIsSubmitting(true);
      const variables = {
        importId,
        importOptions,
      };
      await importExternalIds({ variables });
      setIsSubmitting(false);
    },
  };
}

/** @param {import('./ReadyToImport.generated').ProfileDataReadyPageQuery | undefined} data */
function afterQuery(data) {
  if (!data) return;
  return {
    importData: data?.workspace?.dataImport,
    stats: data?.workspace?.dataImport?.preview,
  };
}

/** @type {typeof import('./ReadyToImport.generated').ProfileDataReadyPageDocument} */
const QUERY = gql`
  query ProductDataReadyPage($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        importId
        importOptions
        preview {
          object
          updatedCount
          unchangedCount
        }
      }
    }
  }
`;

/** @type {typeof import('./ReadyToImport.generated').FinalizeProfileDataImportDocument} */
export const IMPORT_PROFILE_DATA = gql`
  mutation finalizeProfileDataImport(
    $importId: ID!
    $importOptions: ProfileDataImportOptionsInput!
  ) {
    result: finalizeProfileDataImport(
      input: { importId: $importId, importOptions: $importOptions }
    ) {
      code
      success
      message
    }
  }
`;
